import { useCallback } from 'react';
import { PureQueryOptions, Reference } from '@apollo/client';

import { useSetMsgHandledMutation } from 'core/graphql';

/**
 * The «Прочитано и обработано» tick — the ONE action that clears the amber dot
 * (spec 0008 §8), so it owns every cache consequence of that dot moving. Both
 * message surfaces (SlackMessagesPanel's group feed, CommunicationsPanel's
 * thread) go through here; splitting it would let the two drift, which is the
 * class of bug this rework exists to kill.
 *
 * Since spec 0030 the tick also CASCADES: it closes every message above it in
 * the same dialog (read on both directions, handled on inbound). The cascade
 * policy lives here rather than at the call sites for the same reason the hook
 * exists — so no caller can forget to cascade, and, more importantly, so no
 * caller can accidentally cascade an UN-tick (which must stay one row: with no
 * undo, un-marking a prefix would resurrect an unbounded tail of triage work).
 *
 * Four things have to happen, and no single mechanism covers all of them:
 *  1. the clicked bubble — the payload's `message{isHandled isRead}` merges
 *     into the normalized entity by itself;
 *  2. every bubble ABOVE it — patched from the payload's id lists via
 *     cache.modify, so an open thread repaints with no network;
 *  3. the conversation row's dot — the payload's `conversation{…}` merges the
 *     same way (this is why the mutation returns the conversation);
 *  4. the «ждут ответа» queue (`conversationsBrowser(unhandledOnly:true)`) and
 *     the whole-group aggregate (`groupCommsIndex`) — server-derived lists
 *     that NO payload can patch. Hence the eviction below + `badgeRefetch`.
 *
 * @param badgeRefetch  groupCommsIndex refetch descriptor from
 *   `useGroupCommsIndex`. Omit where no group badges are on screen (the
 *   student/teacher detail cards) — then there is nothing to re-pull.
 */
export const useMessageTriage = (
  badgeRefetch?: PureQueryOptions,
): {
  toggleHandled: (messageId: string, handled: boolean) => void;
} => {
  const [setHandled] = useSetMsgHandledMutation();

  const toggleHandled = useCallback(
    (messageId: string, handled: boolean) => {
      setHandled({
        // Cascade on the tick, never on the un-tick — see the hook docstring.
        variables: { messageId, handled, cascade: handled === true },
        refetchQueries: badgeRefetch ? [badgeRefetch] : [],
        update: (cache, { data }) => {
          const payload = data?.setMessageHandled;
          // Patch the rows the cascade flipped. When `truncated`, the lists
          // are partial, so patching them would leave a half-green thread —
          // let the next thread fetch correct it instead.
          if (payload && !payload.truncated) {
            (payload.readMessageIds || []).forEach((id) => {
              cache.modify({
                id: cache.identify({ __typename: 'MessageType', id }),
                fields: { isRead: () => true },
              });
            });
            (payload.handledMessageIds || []).forEach((id) => {
              cache.modify({
                id: cache.identify({ __typename: 'MessageType', id }),
                fields: { isHandled: () => true },
              });
            });
          }
          const conv = payload?.conversation;
          // Only a dialog that just ran out of unhandled inbound messages
          // leaves the queue. Un-ticking must ADD it back, which no local
          // write can do correctly (the server decides ordering/paging) — it
          // resolves on the next fetch of that list.
          if (!conv || conv.unhandledCount !== 0) return;
          const convId = String(conv.id);
          cache.modify({
            fields: {
              conversationsBrowser(
                existing: {
                  edges?: Array<{ node?: Reference | null } | null> | null;
                  totalCount?: number | null;
                },
                { storeFieldName, readField },
              ) {
                // Match "is the unhandled queue" as NOT-false rather than
                // is-true: `unhandledOnly` defaults to true server-side, and
                // Apollo omits undefined variables from storeFieldName, so a
                // caller relying on that default produces a field name with
                // no `unhandledOnly` in it at all. The old is-true test
                // silently skipped exactly those entries, leaving a cleared
                // dialog sitting in the queue.
                if (storeFieldName.includes('"unhandledOnly":false')) {
                  return existing;
                }
                const edges = (existing?.edges || []).filter(
                  (edge) =>
                    String(readField('id', edge?.node || undefined)) !== convId,
                );
                if (edges.length === (existing?.edges || []).length) {
                  return existing;
                }
                return {
                  ...existing,
                  edges,
                  totalCount: Math.max(0, (existing?.totalCount || 1) - 1),
                };
              },
            },
          });
        },
      }).catch(() => {
        // Non-critical: a failed tick leaves the dot lit, which is honest.
      });
    },
    [setHandled, badgeRefetch],
  );

  return { toggleHandled };
};
